import type { CityConfig } from "../city-template/types";

export const mentorConfig: CityConfig = {
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    { label: "Birth & Death Certificates — Ohio Department of Health", href: "https://odh.ohio.gov/know-our-programs/vital-statistics", description: "Official Ohio vital records office in Columbus" },
    { label: "Marriage Licenses — Lake County Probate Court", href: "https://www.lakecountyohio.gov/probate-court", description: "Lake County Probate Judge — marriage license issuance" },
    { label: "Divorce Records — Lake County Clerk of Courts", href: "https://www.lakecountyohio.gov/clerk-of-courts", description: "Lake County Clerk of Courts — divorce decree records" },
    { label: "VitalChek — Order Vital Records Online", href: "https://www.vitalchek.com/", description: "Official partner of Ohio Department of Health for online vital record orders" },
  ],
  city: "Mentor",
  state: "Ohio",
  stateAbbr: "OH",
  slug: "mentor",
  route: "/mentor",
  title: "Emergency Passport Mentor, OH — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Mentor, OH — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Lake County and the eastern Cleveland suburbs. Get your passport fast!",
  h1: "Emergency & Expedited Passport Services in Mentor, OH",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport in Mentor? We help Lake County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Mentor Ohio Lake Erie shoreline with city park and suburban lakefront view, green trees and summer sky over Lake Erie water, calm natural coastal scene with warm golden hour lighting, professional Ohio landscape photography with vibrant colors and clear sky",
  heroImageAlt: "Mentor Ohio Lake Erie shoreline with city park and suburban lakefront view, green trees and summer sky over Lake Erie water, calm natural coastal scene with warm golden hour lighting, professional Ohio landscape photography with vibrant colors and clear sky",
  geoPosition: "41.6662;-81.3396",
  geoPlacename: "Mentor, Ohio",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "230 S Dearborn St, Suite 380, Chicago, IL 60604",
    distance: "Located in downtown Chicago — approximately 350 miles from Mentor via I-90",
  },
  nearbyCitiesZips: [
    { name: "Mentor", zips: "44060, 44061" },
    { name: "Painesville", zips: "44077" },
    { name: "Willoughby", zips: "44094, 44096" },
    { name: "Eastlake", zips: "44095" },
    { name: "Euclid", zips: "44117, 44123, 44132" },
    { name: "Kirtland", zips: "44094" },
    { name: "Concord", zips: "44077" },
    { name: "Madison", zips: "44057" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Mentor departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-global-line", title: "Lake County Coverage", desc: "Serving Mentor, Painesville, Willoughby, and Eastlake" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Cleveland, OH", route: "/cleveland" },
    { name: "Fast passport renewal in Akron, OH", route: "/akron" },
    { name: "Emergency passport help in Elyria, OH", route: "/elyria" },
    { name: "Urgent passport services in Lorain, OH", route: "/lorain" },
    { name: "Same-day passport help in Canton, OH", route: "/canton" },
    { name: "Passport courier services in Youngstown, OH", route: "/youngstown" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Mentor remotely via secure courier. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, achieving 24–48 hour turnaround for Lake County travelers.",
  fpcOfficeDistance: "Serving all of Lake County and the eastern Cleveland suburbs",
  fpcOfficeAddress: "1330 Carnegie Ave, Cleveland, OH 44115",
  serviceAreaIntro: "From Mentor-on-the-Lake to Painesville, Willoughby, and Eastlake, Fast Passport Center serves the entire Lake County region.",
  nearbyCitiesIntro: "We serve all Mentor neighborhoods and the surrounding Lake County communities including Painesville, Willoughby, Eastlake, Euclid, Kirtland, Concord, and Madison.",
  howItWorksIntro: "Most Lake County travelers complete everything online and ship documents overnight. No need to drive to Chicago or wait in line at the regional agency.",
  mistakesIntro: "Mentor travelers often face a long drive to the Chicago Regional Passport Agency. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Lake County travelers trust us when their departure is days away.",
  faqIntro: "Have questions about expedited passports in Mentor? Here are the most common ones we hear from Lake County travelers.",
  statePageRoute: "/states/ohio",
  statePageName: "Ohio",
  dmvWebsite: "https://www.bmv.ohio.gov",
  dmvLabel: "Ohio BMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail",
        "Can't upgrade speed after submission",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Mentor international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens." },
    { name: "New Passport", description: "First-time passport application assistance." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports." },
    { name: "Child Passport", description: "Passport services for minors under 16." },
    { name: "Name Change", description: "Passport correction and name change service." },
  ],
  testimonials: [
    {
      name: "Rachel G.",
      location: "Mentor, OH",
      rating: 5,
      text: "Family cruise to Mexico was 3 weeks away and my passport expired. Fast Passport Center had my renewal in 4 days. Amazing communication every day.",
      service: "Passport Renewal",
    },
    {
      name: "Matt H.",
      location: "Painesville, OH",
      rating: 5,
      text: "Needed an emergency passport for a last-minute business trip. No way was I driving to Chicago. Fast Passport Center handled everything — got it in 36 hours.",
      service: "Emergency Passport",
    },
    {
      name: "Angela B.",
      location: "Willoughby, OH",
      rating: 5,
      text: "My daughter's passport was lost a week before our Europe trip. Fast Passport Center guided us through the DS-64 form and got her replacement in 3 days.",
      service: "Lost Passport",
    },
    {
      name: "John D.",
      location: "Eastlake, OH",
      rating: 5,
      text: "First-time applicant and had no clue where to start. Fast Passport Center found the nearest acceptance facility, reviewed my documents, and got my passport in 5 days.",
      service: "New Passport",
    },
  ],
  facilities: [
    {
      id: "mentor-1",
      name: "Mentor Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "6000 Heisley Rd",
      city: "Mentor",
      state: "OH",
      zip: "44060",
      phone: "(440) 974-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Mentor neighborhood",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient Mentor facility. Call ahead for passport appointments.",
      icon: "ri-mail-line",
    },
    {
      id: "mentor-2",
      name: "Painesville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "77 Richmond St",
      city: "Painesville",
      state: "OH",
      zip: "44077",
      phone: "(440) 352-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Lake County county seat",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Painesville and Concord residents. Ample parking.",
      icon: "ri-mail-line",
    },
    {
      id: "mentor-3",
      name: "Willoughby Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "37001 Euclid Ave",
      city: "Willoughby",
      state: "OH",
      zip: "44094",
      phone: "(440) 942-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Southwest Lake County",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Alternative for Willoughby and Euclid residents. Photo services nearby.",
      icon: "ri-mail-line",
    },
    {
      id: "mentor-4",
      name: "Lake County Clerk of Courts",
      type: "Court Clerk Passport Acceptance",
      address: "105 Main St",
      city: "Painesville",
      state: "OH",
      zip: "44077",
      phone: "(440) 350-2653",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "Lake County Courthouse",
      website: "https://www.lakecountyohio.gov/",
      websiteLabel: "Lake County Government",
      notes: "Accepts passport applications. Good alternative with extended hours.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 5+ hour drive to Chicago", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Passport photos must be exactly 2×2 inches with a white or off-white background. Many local photo shops use wrong specs.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Lake County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many applicants show up with incorrect payment and have to reschedule.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Standard processing takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent for Minors",
      description: "For minors, both parents must appear or submit notarized consent. Don't miss this requirement.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop documents at any UPS location in Mentor." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Mentor address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Mentor before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Mentor for travelers with urgent international flights. Our registered couriers achieve 24–48 hour turnaround without the 5+ hour drive to Chicago." },
    { q: "How fast can I renew my passport in Mentor?", a: "We offer fast passport renewal in Mentor in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value." },
    { q: "What is the closest passport agency to Mentor, OH?", a: "The nearest regional passport agency to Mentor is the Chicago Passport Agency, approximately 350 miles away. For urgent processing without the long drive, Fast Passport Center's registered courier service achieves 24–48 hour turnaround." },
    { q: "Do I need to drive to Chicago for an emergency passport?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities." },
    { q: "Can I expedite a child passport in Mentor?", a: "Yes. We expedite child passports in Mentor for minors under 16. Both parents must appear or submit notarized consent." },
    { q: "What should I do if I lost my passport before a trip?", a: "Contact Fast Passport Center immediately. We provide lost passport replacement services in Mentor with DS-64 form guidance. Our emergency courier service can get your replacement in 24–48 hours." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Mentor", address: "8581 Mentor Ave, Mentor, OH 44060", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Mentor", address: "7200 Mentor Ave, Mentor, OH 44060", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Walmart Supercenter — Mentor", address: "9300 Mentor Ave, Mentor, OH 44060", hours: "Mon–Sun: 6AM–11PM" },
    { name: "FedEx Office — Mentor", address: "7040 Center St, Mentor, OH 44060", hours: "Mon–Fri: 8AM–8PM" },
  ],
  dmvLocations: [
    {
      name: "Lake County BMV — Mentor",
      address: "8400 Plaza Blvd, Mentor, OH 44060",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Driver's license and REAL ID services. Appointment strongly recommended.",
      featured: true,
    },
    {
      name: "Painesville BMV Deputy Registrar",
      address: "33 W Washington St, Painesville, OH 44077",
      hours: "Mon–Fri: 9AM–5PM",
      note: "Alternative for east Lake County residents. REAL ID available.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "CLE",
      name: "Cleveland Hopkins International Airport",
      distance: "25 miles west of Mentor",
      address: "5300 Riverside Dr, Cleveland, OH 44135",
      note: "Major hub with domestic and international routes. Quick access via I-90 west.",
      flightUrl: "https://www.clevelandairport.com/",
      flightLabel: "Cleveland Airport",
      featured: true,
    },
    {
      code: "CAK",
      name: "Akron-Canton Regional Airport",
      distance: "55 miles south of Mentor",
      address: "5400 Lauby Rd NW, North Canton, OH 44720",
      note: "Alternative for domestic flights to major hubs.",
      flightUrl: "https://akroncantonairport.com/",
      flightLabel: "CAK Airport",
      featured: false,
    },
    {
      code: "CMH",
      name: "John Glenn Columbus International Airport",
      distance: "150 miles southwest of Mentor",
      address: "4600 International Gateway, Columbus, OH 43219",
      note: "Larger hub with more international routes. Consider for long-haul trips.",
      flightUrl: "https://www.flycmh.com/",
      flightLabel: "Columbus Airport",
      featured: false,
    },
  ],
};